/**
 * Breadcrumbs Component
 * Phase 2: Product Shell & Navigation
 * 
 * Displays hierarchical navigation path (e.g., Projects > Audit Runs > Audit #123).
 * Provides clear context and navigation to parent pages.
 */

import { Link } from "react-router-dom";
import { Icon } from "./Icon";
import { type RouteConfig } from "../lib/navigation";
import { useLocale } from "../contexts/LocaleContext";

interface BreadcrumbsProps {
  /** Breadcrumb trail from root to current page */
  trail: RouteConfig[];
  /** Optional custom labels for parameterized routes */
  customLabels?: Record<string, string>;
}

export function Breadcrumbs({ trail, customLabels = {} }: BreadcrumbsProps) {
  const { t } = useLocale();
  if (trail.length === 0) {
    return null;
  }

  return (
    <nav
      className="breadcrumbs"
      aria-label={t("common.breadcrumb")}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "13px",
        fontFamily: "var(--reviewer-font-mono)",
        color: "var(--reviewer-text-secondary)",
        padding: "12px 0",
        minHeight: "40px",
      }}
    >
      <ol
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          listStyle: "none",
          padding: 0,
          margin: 0,
        }}
      >
        {trail.map((item, index) => {
          const isLast = index === trail.length - 1;
          const displayLabel = customLabels[item.path] || t(item.labelKey);
          const linkPath = item.path.includes(":") ? item.parentPath || item.path : item.path;

          return (
            <li
              key={`${item.path}-${index}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {index > 0 && (
                <Icon
                  name="chevron-right"
                  size={14}
                  className="breadcrumb-separator"
                  style={{ color: "var(--reviewer-text-tertiary)" }}
                />
              )}
              {isLast ? (
                <span
                  style={{
                    color: "var(--reviewer-text-primary)",
                    fontWeight: 500,
                  }}
                  aria-current="page"
                >
                  {displayLabel}
                </span>
              ) : (
                <Link
                  to={linkPath}
                  style={{
                    color: "var(--reviewer-text-secondary)",
                    textDecoration: "none",
                    transition: "color 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "var(--reviewer-accent)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "var(--reviewer-text-secondary)";
                  }}
                >
                  {displayLabel}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
